import { Button } from '../ui/Button';
import { GlassPanel } from '../ui/GlassPanel';
import { Check, Crown, Zap, CreditCard } from 'lucide-react';
import { LucideIcon } from 'lucide-react';

interface PricingTierCardProps {
  name: string;
  description: string;
  priceMonthly: number;
  priceAnnual?: number;
  features: string[];
  icon?: LucideIcon;
  isPopular?: boolean;
  isCurrent?: boolean;
  billingCycle: 'monthly' | 'annual';
  onSelect: () => void;
  isLoading?: boolean;
}

export function PricingTierCard({
  name,
  description,
  priceMonthly,
  priceAnnual,
  features,
  icon: Icon,
  isPopular,
  isCurrent,
  billingCycle,
  onSelect,
  isLoading,
}: PricingTierCardProps) {
  const price = billingCycle === 'monthly' ? priceMonthly : priceAnnual || priceMonthly * 10;
  const perLocationText = '/location/month';

  return (
    <GlassPanel
      className={`p-8 relative overflow-hidden ${
        isPopular ? 'border-2 border-primary-400/50 scale-105' : ''
      }`}
    >
      {isPopular && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
          <span className="bg-gradient-to-r from-primary-400 to-primary-600 text-white px-6 py-1.5 rounded-full text-sm font-bold shadow-lg">
            Most Popular
          </span>
        </div>
      )}

      <div className="mb-6">
        <div className="flex items-center gap-3 mb-3">
          {Icon && <Icon className="w-7 h-7 text-primary-400" />}
          <h3 className="text-2xl font-bold text-white">{name}</h3>
        </div>
        <p className="text-white/60 text-sm min-h-[40px]">{description}</p>
      </div>

      <div className="mb-6">
        <div className="flex items-baseline gap-2 mb-1">
          <span className="text-5xl font-bold text-white">€{price}</span>
          <span className="text-white/60 text-sm">{perLocationText}</span>
        </div>
        {billingCycle === 'annual' && priceAnnual && (
          <p className="text-sm text-success-400">
            Save €{(priceMonthly * 12 - priceAnnual).toFixed(0)}/year per location
          </p>
        )}
        <p className="text-xs text-white/50 mt-1">
          Billed {billingCycle === 'monthly' ? 'monthly' : 'annually'}
        </p>
      </div>

      <div className="space-y-3 mb-8 max-h-96 overflow-y-auto">
        {features.map((feature, index) => (
          <div key={index} className="flex items-start gap-3 text-sm text-white/80">
            <Check className="w-5 h-5 text-success-400 flex-shrink-0 mt-0.5" />
            <span>{feature}</span>
          </div>
        ))}
      </div>

      {isCurrent ? (
        <Button variant="secondary" className="w-full" disabled>
          Current Plan
        </Button>
      ) : (
        <Button
          className="w-full"
          variant={isPopular ? 'primary' : 'secondary'}
          onClick={onSelect}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/20 border-t-white mr-2" />
              Processing...
            </>
          ) : (
            <>
              <CreditCard className="w-4 h-4 mr-2" />
              Get Started
            </>
          )}
        </Button>
      )}
    </GlassPanel>
  );
}
